import { Box, Typography } from '@mui/material';
import { BackButton, MENU_ICONS, SideMenuLink } from 'components/ui';
import { CalendarClockIcon } from 'components/icons';
import { useIsFullWidth, useProfile } from 'hooks';
import { MainLayout } from 'layouts/MainLayout';
import Head from 'next/head';
import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { Routes } from 'services/routes';
import { Image } from 'components/lib';
import useFeature, { FeatureFlag } from 'hooks/useFeature';
import { GetProfileResponse, useEventsApi, useOrganizationsApi } from 'services/api';
import { RoleMap, useGetRole, Role } from 'hooks/useGetRole';
import useSWR from 'swr';
import AdminRequestIcon from 'assets/icons/admin_request.svg';
import { schoolAdminSsoConfiguration } from 'config/features';
import { MasterPanelPageLayout } from '../master-panel';

export interface AdminPageLayoutProps {
  children?: ReactNode;
  title?: string;
  hideMobileBottomNav?: boolean;
  active:
    | 'organizations'
    | 'users'
    | 'events'
    | 'tickets'
    | 'school details'
    | 'pathways'
    | 'analytics'
    | 'help'
    | 'add-organization'
    | 'saml'
    | 'event-requests'
    | 'permission-requests'
    | 'schedule';
  backgroundColor?: string;
  hidePageTitle?: boolean;
}

const PATHWAYS_CONTENT_STYLES = {
  // Pathways: 30px left inset; 60px right so content lines up
  // with the header profile button (Header mr: 60).
  pt: { xs: 0, md: '30px' },
  pl: { xs: 20, md: '30px' },
  pr: { xs: 20, md: '60px' },
  // Keep above fixed mobile bottom nav (MainLayout default pb: 90)
  pb: { xs: '100px', md: '40px' },
} as const;

const SCHEDULE_CONTENT_STYLES = {
  pt: { xs: '32px', md: '40px' },
  px: { xs: '20px', md: '40px', lg: '40px' },
  pb: { xs: '100px', md: '40px' },
} as const;

const hasAnyRole = (role: Role[], allowed: Role[]): boolean =>
  allowed.some(r => role.includes(r));

const canSeeEventsTab = (
  role: Role[],
  profile: GetProfileResponse | undefined,
): boolean => {
  if (profile?.school?.isMultiStepApprovalEnabled)
    return role.includes(RoleMap['executive-admin']);

  return hasAnyRole(role, [
    RoleMap['executive-admin'],
    RoleMap.admin,
    RoleMap.leader,
    RoleMap.approver,
    RoleMap['sequential-approver'],
  ]);
};

const canSeeScheduleTab = (role: Role[]): boolean =>
  hasAnyRole(role, [
    RoleMap['executive-admin'],
    RoleMap.approver,
    RoleMap['sequential-approver'],
  ]);

const getSchoolSettingsTab = (
  tab: string | string[] | undefined,
): string | null => {
  if (Array.isArray(tab))
    return tab[0] ?? null;
  return tab ?? null;
};

const getAdminTitleMargin = (
  isPathways: boolean,
  isFullWidth: boolean,
): number => {
  if (isPathways)
    return 0;
  return isFullWidth ? 32 : 26;
};

const getAdminTitleStyle = (
  isPathways: boolean,
  isFullWidth: boolean,
): React.CSSProperties => {
  if (isPathways) {
    return {
      fontWeight: 600,
      fontSize: isFullWidth ? 32 : 20,
      color: '#383838',
      letterSpacing: isFullWidth ? '-0.32px' : '-0.1px',
      lineHeight: isFullWidth ? '40px' : '28px',
      fontFamily: 'Poppins, sans-serif',
    };
  }

  return {
    fontWeight: 500,
    fontSize: isFullWidth ? 40 : 20,
    color: '#000000',
    letterSpacing: '-1px',
    lineHeight: '48px',
    fontFamily: 'Poppins, sans-serif',
  };
};

const NavIcon = ({
  alt,
  isActive,
  activeSrc,
  inactiveSrc,
  width = 24,
  height,
}: {
  alt: string;
  isActive: boolean;
  activeSrc: string;
  inactiveSrc: string;
  width?: number;
  height?: number;
}): ReactElement => (
  <Image
    width={width}
    height={height}
    alt={alt}
    src={isActive ? activeSrc : inactiveSrc}
    style={
      height
        ? { objectFit: 'contain', width, height }
        : undefined
    }
  />
);

const getSchoolSettingsNavItem = (
  active: string,
  schoolSettingsExpanded: boolean,
  onToggleSchoolSettings: (e: React.MouseEvent) => void,
  schoolSettingsTab: string | null,
  isMultiStepApprovalEnabled: boolean,
): ReactElement => {
  const schoolSettingsActive =
    active === 'school details' || active === 'pathways';
  const isBrandingTab =
    schoolSettingsTab === 'details' ||
    schoolSettingsTab == null ||
    schoolSettingsTab === '';

  return (
    <SideMenuLink
      key="school-details"
      icon={
        <NavIcon
          alt="School Settings"
          isActive={schoolSettingsActive}
          activeSrc={MENU_ICONS.school}
          inactiveSrc={MENU_ICONS.schoolOutline}
        />
      }
      href={Routes.admin.details()}
      label="School Settings"
      active={schoolSettingsActive}
      expanded={schoolSettingsExpanded}
      onClickExpand={onToggleSchoolSettings}
    >
      <SideMenuLink.SubMenuItem
        label="Pathways"
        href={Routes.admin.pathways()}
        active={active === 'pathways'}
      />
      {isMultiStepApprovalEnabled && (
        <SideMenuLink.SubMenuItem
          label="Event Settings"
          href={`${Routes.admin.details()}?tab=event-setup`}
          active={
            active === 'school details' && schoolSettingsTab === 'event-setup'
          }
        />
      )}
      <SideMenuLink.SubMenuItem
        label="Learning Outcomes"
        href={`${Routes.admin.details()}?tab=learning-outcomes`}
        active={
          active === 'school details' &&
          schoolSettingsTab === 'learning-outcomes'
        }
      />
      <SideMenuLink.SubMenuItem
        label="Branding"
        href={`${Routes.admin.details()}?tab=details`}
        active={active === 'school details' && isBrandingTab}
      />
    </SideMenuLink>
  );
};

const getNavigationItems = (
  role: Role[],
  active: string,
  isAnalyticsPageEnabled: boolean,
  profile: GetProfileResponse | undefined,
  eventRequestsCount: number | undefined,
  adminRequestsCount: number | undefined,
  schoolSettingsExpanded: boolean,
  onToggleSchoolSettings: (e: React.MouseEvent) => void,
  schoolSettingsTab: string | null,
) => {
  const items = [];
  const isExecutiveAdmin = hasAnyRole(role, [RoleMap['executive-admin']]);
  const isOrgAdmin = hasAnyRole(role, [
    RoleMap['executive-admin'],
    RoleMap.admin,
    RoleMap.leader,
  ]);

  if (isOrgAdmin) {
    items.push(
      <SideMenuLink
        key="organizations"
        icon={
          <NavIcon
            alt="Organizations"
            isActive={active === 'organizations'}
            activeSrc={MENU_ICONS.organization}
            inactiveSrc={MENU_ICONS.organizationOutline}
          />
        }
        href={Routes.admin.organizations()}
        label="Organizations"
        active={active === 'organizations'}
      />,
    );
  }

  if (isExecutiveAdmin) {
    items.push(
      <SideMenuLink
        key="add-organization"
        icon={
          <NavIcon
            alt="Add Organization"
            isActive={active === 'add-organization'}
            activeSrc={MENU_ICONS.addOrganization}
            inactiveSrc={MENU_ICONS.addOrganizationOutline}
            width={33}
            height={25}
          />
        }
        href={Routes.admin.createOrganization()}
        label="Add Organization"
        active={active === 'add-organization'}
      />,
    );

    items.push(
      <SideMenuLink
        key="users"
        icon={
          <NavIcon
            alt="Users"
            isActive={active === 'users'}
            activeSrc={MENU_ICONS.users}
            inactiveSrc={MENU_ICONS.usersOutline}
          />
        }
        href={Routes.admin.users()}
        label="Users"
        active={active === 'users'}
      />,
    );

    items.push(
      getSchoolSettingsNavItem(
        active,
        schoolSettingsExpanded,
        onToggleSchoolSettings,
        schoolSettingsTab,
        !!profile?.school?.isMultiStepApprovalEnabled,
      ),
    );

    if (isAnalyticsPageEnabled) {
      items.push(
        <SideMenuLink
          key="analytics"
          icon={
            <NavIcon
              alt="Analytics"
              isActive={active === 'analytics'}
              activeSrc={MENU_ICONS.analytics}
              inactiveSrc={MENU_ICONS.analyticsOutline}
            />
          }
          href={Routes.admin.analytics()}
          label="Analytics"
          active={active === 'analytics'}
        />,
      );
    }

    if (schoolAdminSsoConfiguration) {
      items.push(
        <SideMenuLink
          key="saml"
          icon={
            <NavIcon
              alt="SAML Configuration"
              isActive={active === 'saml'}
              activeSrc={MENU_ICONS.security}
              inactiveSrc={MENU_ICONS.securityOutline}
            />
          }
          href={Routes.admin.saml()}
          label="SAML SSO"
          active={active === 'saml'}
        />,
      );
    }
  }

  if (
    profile?.school?.isMultiStepApprovalEnabled &&
    hasAnyRole(role, [
      RoleMap['executive-admin'],
      RoleMap.admin,
      RoleMap.approver,
      RoleMap['sequential-approver'],
    ])
  ) {
    items.push(
      <SideMenuLink
        key="event-requests"
        icon={
          <NavIcon
            alt="Events Requests"
            isActive={active === 'event-requests'}
            activeSrc={MENU_ICONS.eventsSettings}
            inactiveSrc={MENU_ICONS.eventsSettingsOutline}
          />
        }
        href={Routes.admin.eventRequests()}
        label="Events Requests"
        active={active === 'event-requests'}
        badge={eventRequestsCount}
      />,
    );
  }

  if (canSeeEventsTab(role, profile)) {
    items.push(
      <SideMenuLink
        key="events"
        icon={
          <NavIcon
            alt="Events"
            isActive={active === 'events'}
            activeSrc={MENU_ICONS.eventsSettings}
            inactiveSrc={MENU_ICONS.eventsSettingsOutline}
          />
        }
        href={Routes.admin.events()}
        label="Events"
        active={active === 'events'}
      />,
    );
  }

  if (canSeeScheduleTab(role)) {
    items.push(
      <SideMenuLink
        key="schedule"
        icon={
          <CalendarClockIcon
            sx={{
              fontSize: 24,
              color: active === 'schedule' ? '#23305F' : '#5F6375',
            }}
          />
        }
        href={Routes.admin.schedule()}
        label="Schedule"
        active={active === 'schedule'}
      />,
    );
  }

  // PERMISSION REQUESTS (Admin Access + Organization Leader Access + Member Access)
  // Org Leaders can see the page, but only member-type requests for their orgs.
  if (isOrgAdmin) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const permissionRequestsHref = Routes.admin.permissionRequests();
    items.push(
      <SideMenuLink
        key="permission-requests"
        icon={<AdminRequestIcon />}
        href={permissionRequestsHref}
        label="Permission Requests"
        active={active === 'permission-requests'}
        badge={adminRequestsCount}
      />,
    );
  }

  if (isOrgAdmin) {
    items.push(
      <SideMenuLink
        key="help"
        icon={
          <NavIcon
            alt="Help"
            isActive={active === 'help'}
            activeSrc={MENU_ICONS.help}
            inactiveSrc={MENU_ICONS.helpOutline}
          />
        }
        href={Routes.admin.help()}
        label="Help"
        active={active === 'help'}
      />,
    );
  }

  return items;
};

export const AdminPageLayout = ({
  children,
  title,
  active,
  hideMobileBottomNav,
  backgroundColor,
  hidePageTitle,
}: AdminPageLayoutProps): ReactElement => {
  const role = useGetRole();
  const router = useRouter();
  const { profile, loading } = useProfile();
  const isFullWidth = useIsFullWidth();
  const isAnalyticsPageEnabled = useFeature(FeatureFlag.AdminAnalyticsPage);
  const eventsApi = useEventsApi();
  const organizationsApi = useOrganizationsApi();

  const isPathways = active === 'pathways';
  const schoolSettingsActive =
    active === 'school details' || isPathways;
  const [schoolSettingsExpanded, setSchoolSettingsExpanded] = useState(
    schoolSettingsActive,
  );

  useEffect(() => {
    if (schoolSettingsActive)
      setSchoolSettingsExpanded(true);
  }, [schoolSettingsActive]);

  const schoolSettingsTab = getSchoolSettingsTab(router.query.tab);

  const shouldShowEventRequests = Boolean(
    profile?.school?.isMultiStepApprovalEnabled &&
      role &&
      hasAnyRole(role, [
        RoleMap['executive-admin'],
        RoleMap.admin,
        RoleMap.approver,
        RoleMap['sequential-approver'],
      ]),
  );

  const { data: eventRequestsCountData } = useSWR<{ count: number }>(
    shouldShowEventRequests ? '/events/event-requests/count' : null,
    async () => {
      const result = await eventsApi.getEventRequestsNeedingReviewCount({
        schoolId: profile?.school?.id || null,
      });
      if (!result || !result.success) return { count: 0 };
      return result.value;
    },
    {
      revalidateOnFocus: true,
      refreshInterval: 30000, // Refresh every 30 seconds
    },
  );

  const eventRequestsCount = eventRequestsCountData?.count ?? 0;

  const shouldShowPermissionRequests = Boolean(
    role &&
      hasAnyRole(role, [
        RoleMap['executive-admin'],
        RoleMap.admin,
        RoleMap.leader,
      ]),
  );

  const { data: permissionRequestsData } = useSWR<{ count: number }>(
    shouldShowPermissionRequests ? '/permission-requests/count' : null,
    async () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const result = await organizationsApi.getPermissionRequestsCount() as { success: boolean; value?: { count: number } };
      if (!result.success) return { count: 0 };
      return { count: result.value?.count ?? 0 };
    },
    {
      revalidateOnFocus: true,
      refreshInterval: 10000, // Refresh every 10 seconds
    },
  );

  const adminRequestsCount = permissionRequestsData?.count ?? 0;

  const navContent = useMemo(
    () => (
      <Box
        height="100%"
        width="100%"
        sx={{
          borderRight: isFullWidth ? '1px solid #E4E6EB' : 'none',
          py: '32px',
          backgroundColor: '#FFFFFF',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '18px',
            lineHeight: '26px',
            fontWeight: 600,
            color: '#383838',
            px: '24px',
            pb: '16px',
          }}
        >
          Admin Portal
        </Typography>
        {role && (
          <Box
            component="nav"
            role="navigation"
            p={0}
            display="flex"
            flexDirection="column"
            gap="4px"
          >
            {getNavigationItems(
              role,
              active,
              isAnalyticsPageEnabled,
              profile,
              eventRequestsCount,
              adminRequestsCount,
              schoolSettingsExpanded,
              e => {
                e.preventDefault();
                e.stopPropagation();
                setSchoolSettingsExpanded(v => !v);
              },
              schoolSettingsTab,
            )}
          </Box>
        )}
      </Box>
    ),
    [
      role,
      isFullWidth,
      active,
      isAnalyticsPageEnabled,
      eventRequestsCount,
      adminRequestsCount,
      schoolSettingsExpanded,
      schoolSettingsTab,
      profile,
    ],
  );

  if (loading) return <MainLayout />;

  if (profile?.role.includes(RoleMap.master)) {
    return (
      <MasterPanelPageLayout active={active} title={title}>
        {children}
      </MasterPanelPageLayout>
    );
  }

  return (
    <MainLayout
      hideMobileBottomNav={hideMobileBottomNav}
      backgroundColor={backgroundColor}
      contentChildrenStyles={
        isPathways
          ? PATHWAYS_CONTENT_STYLES
          : active === 'schedule'
            ? SCHEDULE_CONTENT_STYLES
            : undefined
      }
    >
      <Head>
        <title>{title}</title>
      </Head>

      {isFullWidth && (
        <MainLayout.Sidebar side="left" fixed width={260}>
          {navContent}
        </MainLayout.Sidebar>
      )}

      {title && !hidePageTitle && (
        <Box
          mt={getAdminTitleMargin(isPathways, isFullWidth)}
          display="flex"
          alignItems="center"
        >
          {!isFullWidth && (
            <Box mr={10}>
              <BackButton />
            </Box>
          )}
          <Typography
            variant="h2"
            style={getAdminTitleStyle(isPathways, isFullWidth)}
          >
            {title}
          </Typography>
        </Box>
      )}
      {children}
    </MainLayout>
  );
};
